import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { appointmentService } from '../services/appointmentService';

export const useAppointments = (filters = {}) => {
  const queryClient = useQueryClient();

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['appointments', filters],
    queryFn: async () => {
      const response = await appointmentService.getAppointments({ ...filters, hasAppointment: true });
      const leads = response.data?.data || response.data?.leads || response.data || [];
      return Array.isArray(leads) ? leads : [];
    },
    staleTime: 15 * 60 * 1000, // 15 minutes: Data remains fresh, satisfying "revisits after a long time"
    cacheTime: 30 * 60 * 1000, // 30 minutes in cache
    refetchOnMount: false, // Don't refetch on mount if data is already in cache and not stale
    refetchOnWindowFocus: false, // Don't refetch when switching browser tabs
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }) => appointmentService.updateAppointment(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['appointments']);
      // also invalidate leads if we want other tabs to refresh
      queryClient.invalidateQueries(['leads']); 
    }
  });

  const deleteMutation = useMutation({
    mutationFn: (id) => appointmentService.removeAppointment(id),
    onSuccess: () => {
      queryClient.invalidateQueries(['appointments']);
      queryClient.invalidateQueries(['leads']);
    }
  });

  return {
    appointments: data || [],
    isLoading,
    error,
    refetch,
    updateAppointment: updateMutation.mutateAsync,
    deleteAppointment: deleteMutation.mutateAsync,
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
  };
};
